import React, { useCallback, useEffect, useState } from 'react';
import { Landmark } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  BackfillReport,
  businessAccountsApi,
  LedgerRepairReport,
} from '../../api/business-accounts';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatCard } from '../../components/ui/StatCard';

/**
 * Opening the accounts that should already exist.
 *
 * A business is given an account in its director's name the moment that
 * director's BVN is confirmed. The ones that verified before that shipped got
 * nothing, and nothing in the normal course of using the app will ever open
 * one for them — the trigger is a verification they already completed. This
 * page is that trigger, applied by hand.
 *
 * ## Every backfill lives here
 *
 * Two panels, fixing different things. Provisioning opens accounts that were
 * never opened; the ledger repairs write rows that were never written.
 *
 * **The repair list is not written here.** The backend owns which repairs
 * exist and describes each one — label, what it fixes, how many are
 * outstanding — and this renders whatever it is given. A backfill added
 * server-side appears on this page with no change to it, which is the whole
 * reason the response is a list rather than a set of named counts.
 *
 * Both duplicate work the migrations do on deploy, and that is the point: a
 * migration runs once and says nothing, so "the backfill didn't work" could
 * mean it ran and matched nothing or that it never ran. These say which,
 * before anybody writes a curl command.
 *
 * ## This replaces Business Wallets
 *
 * That page watched a pooled Safe Haven account holding money for businesses
 * that had none of their own, and led with the comparison between what the
 * ledger said was owed and what the bank said was there. There is no pool any
 * more — it was emptied and dropped — so the page had no subject left. What
 * survived it is the one action that fixed the businesses it used to list.
 *
 * ## Preview, then run
 *
 * Two actions rather than a confirm dialog on one. A dialog asks "are you
 * sure?" about something whose size you cannot see; the preview answers "how
 * many businesses?" before anything happens, which is the question worth
 * asking. Run stays disabled until it has been used.
 */
export const BusinessAccounts: React.FC = () => {
  usePage('Business Accounts');
  const toast = useToast();

  const [report, setReport] = useState<BackfillReport | null>(null);
  const [mode, setMode] = useState<'preview' | 'run' | null>(null);
  const [busy, setBusy] = useState(false);

  const [ledger, setLedger] = useState<LedgerRepairReport | null>(null);
  const [ledgerBusy, setLedgerBusy] = useState(false);

  const preview = useCallback(async () => {
    setBusy(true);
    try {
      setReport(await businessAccountsApi.previewBackfill());
      setMode('preview');
    } catch (err) {
      toast.error(err, 'Could not preview the backfill.');
    } finally {
      setBusy(false);
    }
  }, [toast]);

  const previewLedger = useCallback(async () => {
    setLedgerBusy(true);
    try {
      setLedger(await businessAccountsApi.previewLedger());
    } catch (err) {
      toast.error(err, 'Could not read the ledger.');
    } finally {
      setLedgerBusy(false);
    }
  }, [toast]);

  useEffect(() => {
    // Both previews open nothing and move nothing, so they are safe on load
    // — and it means the page answers "is anything outstanding?" without
    // anybody pressing a button.
    void preview();
    void previewLedger();
  }, [preview, previewLedger]);

  /** How much is left to fix, across every repair the backend reports. */
  const outstanding = ledger?.outstanding ?? 0;

  const repairLedger = async (key?: string) => {
    setLedgerBusy(true);
    try {
      const result = await businessAccountsApi.repairLedger(key);
      // A single-repair run only reports on that one, so the others' counts
      // would be dropped. Re-reading keeps the page whole.
      setLedger(key ? await businessAccountsApi.previewLedger() : result);
      toast.success(
        result.repaired === 0
          ? 'Nothing needed fixing'
          : `${result.repaired} row${result.repaired === 1 ? '' : 's'} written`,
      );
    } catch (err) {
      toast.error(err, 'The repair did not run.');
    } finally {
      setLedgerBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const result = await businessAccountsApi.runBackfill();
      setReport(result);
      setMode('run');
      if (result.failed > 0) {
        toast.error(
          `${result.failed} business${result.failed === 1 ? '' : 'es'} could not be given an account`,
        );
      } else {
        toast.success(
          `${result.provisioned} account${result.provisioned === 1 ? '' : 's'} opened`,
        );
      }
    } catch (err) {
      toast.error(err, 'The backfill did not run.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card padding={20} style={{ marginBottom: 20 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ maxWidth: 640 }}>
            <h2
              style={{
                fontSize: 15,
                fontWeight: 600,
                margin: '0 0 6px',
                color: 'var(--neutral-900)',
              }}
            >
              Open outstanding accounts
            </h2>
            <p
              style={{
                fontSize: 13,
                lineHeight: 1.55,
                margin: 0,
                color: 'var(--neutral-700)',
              }}
            >
              A business gets an account in its director&apos;s name when that
              director&apos;s BVN is confirmed. These verified before that
              existed, so nothing has ever opened one for them.
              <br />
              Safe to run more than once — a business that already has an
              account is skipped, not re-provisioned.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={preview} disabled={busy}>
              Refresh
            </Button>
            <Button
              onClick={run}
              disabled={busy || report?.considered === 0}
              title={
                report?.considered === 0
                  ? 'Nothing outstanding'
                  : 'Opens real bank accounts'
              }
            >
              {busy ? 'Working…' : 'Open accounts'}
            </Button>
          </div>
        </div>
      </Card>

      {/* The ledger repairs. Separate from account provisioning because they
          fix different things: one opens accounts that were never opened,
          this writes rows that were never written. Grouped here so every
          backfill is in one place rather than a curl command in a PR. */}
      <Card padding={20} style={{ marginBottom: 20 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: ledger ? 16 : 0,
          }}
        >
          <div style={{ maxWidth: 640 }}>
            <h2
              style={{
                fontSize: 15,
                fontWeight: 600,
                margin: '0 0 6px',
                color: 'var(--neutral-900)',
              }}
            >
              Ledger repairs
            </h2>
            <p
              style={{
                fontSize: 13,
                lineHeight: 1.55,
                margin: 0,
                color: 'var(--neutral-700)',
              }}
            >
              Rows the ledger should have and doesn&apos;t — a transfer stuck
              at &ldquo;pending&rdquo;, a VAT charge with no entry, a reversal
              with no credit, a payment whose sender bank was never resolved.
              <br />
              The migrations do the same work on deploy. This says whether
              anything is left, and is safe to run more than once.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="secondary"
              onClick={previewLedger}
              disabled={ledgerBusy}
            >
              Refresh
            </Button>
            <Button
              onClick={() => void repairLedger()}
              disabled={ledgerBusy || outstanding === 0}
              title={outstanding === 0 ? 'Nothing outstanding' : undefined}
            >
              {ledgerBusy ? 'Working…' : 'Repair'}
            </Button>
          </div>
        </div>

        {ledger && (
          <div style={{ display: 'grid', gap: 8 }}>
            {/* Rendered from whatever the backend reports rather than a fixed
                set of cards. A repair added server-side shows up here on its
                own — which is the point of the list being a list. */}
            {ledger.repairs.map((r) => (
              <div
                key={r.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 16,
                  padding: '12px 14px',
                  borderRadius: 'var(--radius-sm)',
                  background:
                    r.outstanding > 0
                      ? 'var(--warning-100)'
                      : 'var(--neutral-100)',
                }}
              >
                <div style={{ maxWidth: 560 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--neutral-900)',
                    }}
                  >
                    {r.label}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      lineHeight: 1.5,
                      marginTop: 3,
                      color: 'var(--neutral-600)',
                    }}
                  >
                    {r.description}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexShrink: 0,
                  }}
                >
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color:
                          r.outstanding > 0
                            ? 'var(--warning-900)'
                            : 'var(--neutral-700)',
                      }}
                    >
                      {r.outstanding}
                    </div>
                    {r.repaired !== undefined && r.repaired > 0 ? (
                      <div style={{ fontSize: 11, color: 'var(--green-700)' }}>
                        {r.repaired} fixed
                      </div>
                    ) : null}
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => void repairLedger(r.key)}
                    disabled={ledgerBusy || r.outstanding === 0}
                  >
                    Fix
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {report && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 14,
              marginBottom: 20,
            }}
          >
            <StatCard
              label={mode === 'run' ? 'Considered' : 'Outstanding'}
              value={String(report.considered)}
            />
            <StatCard label="Opened" value={String(report.provisioned)} />
            <StatCard label="Skipped" value={String(report.skipped)} />
            <StatCard label="Failed" value={String(report.failed)} />
          </div>

          <Card padding={20}>
            {report.considered === 0 ? (
              <EmptyState
                icon={Landmark}
                title="Nothing outstanding"
                description="Every business with a verified director already banks for itself."
              />
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {report.outcomes.map((o) => (
                  <div
                    key={o.businessId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 12px',
                      borderRadius: 'var(--radius-sm)',
                      background:
                        o.result === 'failed'
                          ? 'var(--danger-100)'
                          : 'var(--neutral-100)',
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <div style={{ color: 'var(--neutral-900)' }}>
                        {o.businessName}
                      </div>
                      {o.reason && (
                        <div
                          style={{
                            fontSize: 12,
                            marginTop: 2,
                            color: 'var(--neutral-600)',
                          }}
                        >
                          {o.reason}
                        </div>
                      )}
                    </div>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        color: 'var(--neutral-700)',
                      }}
                    >
                      {o.accountNumber && <span>{o.accountNumber}</span>}
                      <Badge
                        variant={
                          o.result === 'failed'
                            ? 'danger'
                            : o.result === 'skipped'
                              ? 'neutral'
                              : 'success'
                        }
                      >
                        {mode === 'preview' && o.result === 'provisioned'
                          ? 'would open'
                          : o.result}
                      </Badge>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
};
